import { DayCapacityData } from "core/periodCapacityData/periodCapacityData.entity";
import { SetStateAction, Dispatch } from "react";
import {
  OneDayInMS,
  dateObjToJalali,
  dateToWeekDay,
  epochToDateType,
  getJalaliDateObject,
  getMonthDays,
  isAfterToday,
  isBeforeToday,
  jalaliToEpoch,
} from "utils/time";
import { toast } from "react-toastify";
import styles from "./CapacityDatePicker.module.scss";
import { useAppSelector } from "utils/store";
import { strings } from "intl/fa";
import { digitsToHindi } from "@bale/utils";
import { isCapacityFull } from "utils/utils";
import classNames from "classnames";
import { LARGE_ORDER_BREAKPOINT } from "utils/constants";

interface DatePickerItemsPropTypes {
  capacityData: DayCapacityData[];
  previewMode?: boolean;
  selectedDate?: number;
  setSelectedDate?: Dispatch<SetStateAction<number | undefined>>;
  viewCount: number;
  disableCapacity?: boolean;
  daysStatus: boolean[];
}

export default function DatePickerItems({
  capacityData,
  previewMode,
  selectedDate,
  setSelectedDate,
  viewCount,
  disableCapacity,
  daysStatus,
}: DatePickerItemsPropTypes) {
  const configs = useAppSelector((state) => state.configs.configs);
  const todaysEpoch = jalaliToEpoch(getJalaliDateObject());
  const validStart = todaysEpoch + configs.ad_start_delay * OneDayInMS;

  const firstDate = capacityData[0]?.date;
  const numberOfMonthDays = getMonthDays(
    epochToDateType(firstDate || todaysEpoch).year,
    epochToDateType(firstDate || todaysEpoch).month
  );

  const largeOrderEndEpoch =
    (selectedDate || 0) +
    Math.ceil(viewCount / LARGE_ORDER_BREAKPOINT) * OneDayInMS;

  const isLargeOrder = !!Math.floor(viewCount / LARGE_ORDER_BREAKPOINT);
  return (
    <>
      {capacityData.slice(0, numberOfMonthDays).map((capData, index) => {
        return (
          <DatePickerItem
            key={index}
            capData={capData}
            selectedDate={selectedDate}
            previewMode={previewMode}
            setSelectedDate={setSelectedDate}
            largeOrderEndEpoch={largeOrderEndEpoch}
            isDisabled={daysStatus[index]}
          />
        );
      })}
    </>
  );
}

interface DatePickerItemPropTypes {
  capData: DayCapacityData;
  selectedDate?: number;
  previewMode?: boolean;
  setSelectedDate?: Dispatch<SetStateAction<number | undefined>>;
  largeOrderEndEpoch: number;
  isDisabled: boolean;
}

function DatePickerItem({
  capData,
  selectedDate,
  previewMode,
  setSelectedDate,
  largeOrderEndEpoch,
  isDisabled,
}: DatePickerItemPropTypes) {
  const jalaliDate = epochToDateType(capData.date);
  const dateObj = new Date(capData.date);
  const weekDay = dateToWeekDay(dateObj); // دوشنبه

  const isSelected = selectedDate === capData.date;


  const dayClickHandler = () => {
    if (previewMode) {
      toast.info(strings.dialog_ad_form.publish_step.requires_approve, {
        toastId: "date_picker_toast",
      });
      return;
    }
    if (isDisabled && isAfterToday(epochToDateType(capData.date))) {
      toast.info(strings.ad_time_select.day_not_selectable, {
        toastId: "date_picker_toast",
      });
      return;
    }
    if (isDisabled) {
      toast.info(strings.dialog_ad_form.publish_step.date_not_selectable, {
        toastId: "date_picker_toast",
      });
      return;
    }
    setSelectedDate?.(capData.date);
  };

  // for large orders
  const isTrailing =
    capData.date > (selectedDate || 0) && capData.date < largeOrderEndEpoch;

  return (
    <div
      data-testid={`picker-item-${capData.date}`}
      className={classNames(styles.DayItem, {
        [styles.Disabled]: isDisabled,
        [styles.Selected]: isSelected,
        [styles.Trailing]: isTrailing,
      })}
      data-disabled={isDisabled}
      onClick={dayClickHandler}
      key={`dateitem-${capData.date}`}
    >
      <div className={classNames(styles.weekDay)}>{weekDay}</div>
      <div>{digitsToHindi(String(jalaliDate.day))}</div>
    </div>
  );
}

const MAX_DAILY_SPONSORED = 500_000;

export const isDayDisabled = (params: {
  dateObj: Date,
  isLargeOrder: boolean,
  capData: DayCapacityData,
  segments: number[],
  validStart: number,
  finalViewCount: number,
  isSponsored: boolean
}
) => {
  const { finalViewCount, validStart, dateObj, isLargeOrder, capData, isSponsored, segments } = params;
  const jalali = dateObjToJalali(dateObj);

  let isSponsoredDisbaled = false;
  if (isBeforeToday({
    year: jalali.jy,
    month: jalali.jm,
    day: jalali.jd,
  })) {
    isSponsoredDisbaled = true;
  }
  if (isLargeOrder &&
    capData.sponsored_reserved_views + LARGE_ORDER_BREAKPOINT >=
    MAX_DAILY_SPONSORED) {
    isSponsoredDisbaled = true;
  }
  if (!isLargeOrder &&
    capData.sponsored_reserved_views + finalViewCount >= MAX_DAILY_SPONSORED) {
    isSponsoredDisbaled = true;
  }
  if (validStart > capData.date) {
    isSponsoredDisbaled = true;
  }
  if (isCapacityFull(capData.segment_capacity_used, segments)) {
    isSponsoredDisbaled = true;
  }


  if (isSponsored) {
    return isSponsoredDisbaled;
  }
  let isDialogDisabled = false;
  if (isBeforeToday({
    year: jalali.jy,
    month: jalali.jm,
    day: jalali.jd,
  })) {
    isDialogDisabled = true;
  }
  if (isLargeOrder &&
    capData.current_reserved_views + LARGE_ORDER_BREAKPOINT >=
    capData.max_views) {
    isDialogDisabled = true;
  }
  if (!isLargeOrder &&
    capData.current_reserved_views + finalViewCount >= capData.max_views) {
    isDialogDisabled = true;
  }
  if (validStart > capData.date) {
    isDialogDisabled = true;
  }
  if (isCapacityFull(capData.segment_capacity_used, segments)) {
    isDialogDisabled = true;
  }
  return isDialogDisabled;
};
